/**
 * Password submission, forgot-password and resend-code hooks (split from loginSubmissionHooks for max-lines).
 */

import { useCallback } from "react";
import { ValidationError } from "class-validator";
import Auth, { type DeliveryChannel } from "@Front/Api/Auth/Customer/Auth";
import { LoginStep } from "../loginSteps";
import { handlePasswordError } from "./loginErrorHandlers";
import { runAskNewPasswordUiFlow } from "./loginAskNewPasswordUi";
import {
	buildCustomerLoginAuthScopePayload,
	type CustomerAuthDisambiguation,
} from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";
export interface UsePasswordSubmissionsOptions {
	totpCode: string;
	email: string;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	handlePasswordValidation: (values: { [key: string]: string }) => ValidationError[] | null;
	connectAndRedirect: (token: { accessToken: string; refreshToken: string }) => Promise<void>;
	setValidationErrors: (errors: ValidationError[]) => void;
}

type SubmitNewPasswordInput = {
	values: { [key: string]: string };
	totpCode: string;
	email: string;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	connectAndRedirect: (token: { accessToken: string; refreshToken: string }) => Promise<void>;
};

async function submitNewPasswordFlow(input: SubmitNewPasswordInput): Promise<void> {
	const { values, totpCode, email, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, connectAndRedirect } = input;
	const token = await Auth.getInstance().setPassword({
		totpCode,
		email,
		password: values["password"] ?? "",
		...buildCustomerLoginAuthScopePayload(loginFolderUid, loginOfficeUid, authDisambiguation, loginCustomerUid),
	});
	await connectAndRedirect(token);
}

async function submitPasswordLoginFlow(input: {
	totpCode: string;
	email: string;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	connectAndRedirect: (token: { accessToken: string; refreshToken: string }) => Promise<void>;
}): Promise<void> {
	const { totpCode, email, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, connectAndRedirect } = input;
	const token = await Auth.getInstance().login({
		totpCode,
		email,
		...buildCustomerLoginAuthScopePayload(loginFolderUid, loginOfficeUid, authDisambiguation, loginCustomerUid),
	});
	await connectAndRedirect(token);
}

type PasswordFormSubmitHandler = (
	e: React.FormEvent<HTMLFormElement> | null,
	values: { [key: string]: string },
) => Promise<void>;

async function trySubmitNewPasswordFromForm(
	values: { [key: string]: string },
	opts: UsePasswordSubmissionsOptions,
): Promise<void> {
	if (!values["password"] || !values["confirm_password"]) return;
	const validationError = opts.handlePasswordValidation(values);
	if (validationError) {
		opts.setValidationErrors(validationError);
		return;
	}
	await submitNewPasswordFlow({
		values,
		totpCode: opts.totpCode,
		email: opts.email,
		loginFolderUid: opts.loginFolderUid,
		loginOfficeUid: opts.loginOfficeUid,
		loginCustomerUid: opts.loginCustomerUid,
		authDisambiguation: opts.authDisambiguation,
		connectAndRedirect: opts.connectAndRedirect,
	});
}

async function runNewPasswordSubmitHandlerAttempt(
	values: { [key: string]: string },
	opts: UsePasswordSubmissionsOptions,
): Promise<void> {
	try {
		await trySubmitNewPasswordFromForm(values, opts);
	} catch (error: unknown) {
		handlePasswordError(error, opts.email, "password", opts.setValidationErrors);
	}
}

function useNewPasswordSubmitCallback(opts: UsePasswordSubmissionsOptions): PasswordFormSubmitHandler {
	return useCallback(
		async (_e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => {
			await runNewPasswordSubmitHandlerAttempt(values, opts);
		},
		[opts],
	);
}

function usePasswordLoginSubmitCallback(opts: UsePasswordSubmissionsOptions): PasswordFormSubmitHandler {
	const { totpCode, email, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, connectAndRedirect, setValidationErrors } = opts;
	return useCallback(
		async (_e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => {
			try {
				if (!values["password"]) return;
				await submitPasswordLoginFlow({
					totpCode,
					email,
					loginFolderUid,
					loginOfficeUid,
					loginCustomerUid,
					authDisambiguation,
					connectAndRedirect,
				});
			} catch (error: unknown) {
				handlePasswordError(error, email, "password", setValidationErrors);
			}
		},
		[email, totpCode, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, connectAndRedirect, setValidationErrors],
	);
}

/**
 * Hook for password submissions
 */
export function usePasswordSubmissions(opts: UsePasswordSubmissionsOptions): {
	onNewPasswordSubmit: (e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => Promise<void>;
	onPasswordSubmit: (e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => Promise<void>;
} {
	return {
		onNewPasswordSubmit: useNewPasswordSubmitCallback(opts),
		onPasswordSubmit: usePasswordLoginSubmitCallback(opts),
	};
}

export interface UsePasswordForgotOptions {
	email: string;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	setPartialPhoneNumber: (value: string) => void;
	setDeliveryChannel: (channel: DeliveryChannel) => void;
	setMaskedEmail: (email: string) => void;
	setTotpCodeUid: (uid: string) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	setStep: (step: LoginStep) => void;
}

/**
 * Hook for password forgot
 */
export function usePasswordForgot(opts: UsePasswordForgotOptions): () => Promise<void> {
	return useCallback(async () => {
		await runAskNewPasswordUiFlow(opts);
	}, [opts]);
}

